import { ReactNode } from 'react';
import cn from 'classnames';
import {
  useTabState,
  Tab as ReakitTab,
  TabList as ReakitTabList,
  TabPanel,
  TabOptions,
} from 'reakit/Tab';

import styles from '@/components/renderers/Code.module.css';
import Code from '@/components/renderers/CodeBlock';
import { CodeBlock } from '@/types';
import TabList from './TabList';

type TabProps = TabOptions & {
  isActive: boolean;
  children: ReactNode;
  className?: string;
};

function Tab({ children, isActive, className, ...rest }: TabProps) {
  return (
    <ReakitTab
      {...rest}
      className={cn(
        `focus:outline-none rounded p-3 text-center text-xs font-medium uppercase tracking-wider lg:text-sm`,
        {
          'bg-light-bunting text-white': isActive,
          'text-light-haiti': !isActive,
        },
        className,
      )}
    >
      {children}
    </ReakitTab>
  );
}

function NavTab({ isActive, children, ...rest }: TabProps) {
  return (
    <ReakitTab
      className={cn(`focus:outline-none border-b pb-1 text-left text-sm`, {
        'border-transparent text-light-haiti': !isActive,
        'border-white text-white': isActive,
      })}
      {...rest}
    >
      {children}
    </ReakitTab>
  );
}

type CodeTabProps = {
  query: CodeBlock['query'];
  response: CodeBlock['response'];
};

function CodeTab({ response, query }: CodeTabProps) {
  const tab = useTabState();

  return (
    <div
      className={cn(
        `h-72 flex-shrink-0 overflow-auto rounded border border-light-bunting bg-big-stone dark:bg-gray-900 lg:h-84`,
        styles.code,
      )}
    >
      <div className="relative h-full">
        <div className="sticky top-0 z-10 bg-big-stone px-3 py-3">
          <ReakitTabList {...tab} aria-label="Query and response">
            <Tab
              {...tab}
              id="gql-query"
              isActive={tab.selectedId === `gql-query`}
              className="mr-2"
            >
              Query
            </Tab>
            <Tab
              {...tab}
              id="response"
              isActive={tab.selectedId === `response`}
            >
              Response
            </Tab>
          </ReakitTabList>
        </div>

        <TabPanel
          {...tab}
          className="focus:outline-none text-sm"
          style={{ height: `calc(100% - 70px)` }}
        >
          <Code className="graphql" noCopy>
            {query || ``}
          </Code>
        </TabPanel>

        <TabPanel
          {...tab}
          className="focus:outline-none text-sm"
          style={{ height: `calc(100% - 70px)` }}
        >
          <Code className="json" noCopy>
            {response || ``}
          </Code>
        </TabPanel>
      </div>
    </div>
  );
}

type NavProps = {
  label: string;
  items: CodeBlock[];
};

function Nav({ items, label }: NavProps) {
  const tab = useTabState();

  return (
    <>
      <TabList
        tab={tab}
        aria-label={`${label} code examples`}
        className="mb-6 h-8"
      >
        <div className="space-x-3">
          {items.map((item) => {
            const isActive = tab.selectedId === item.label;

            return (
              <NavTab
                key={item.label}
                isActive={isActive}
                id={item.label}
                {...tab}
              >
                {item.label}
              </NavTab>
            );
          })}
        </div>
      </TabList>

      {items.map((item) => (
        <TabPanel key={item.label} {...tab} className="focus:outline-none">
          <CodeTab response={item.response} query={item.query} />
        </TabPanel>
      ))}
    </>
  );
}

type PlaygroundProps = {
  queries: CodeBlock[];
  mutations: CodeBlock[];
};

export default function Playground({ queries, mutations }: PlaygroundProps) {
  const tab = useTabState();

  return (
    <div className="w-full overflow-auto text-white">
      <ReakitTabList
        {...tab}
        aria-label="Queries and Mutations examples"
        className="mb-6"
      >
        <Tab
          {...tab}
          isActive={tab.selectedId === `queries`}
          id="queries"
          className="mr-2"
        >
          Queries
        </Tab>
        <Tab {...tab} isActive={tab.selectedId === `mutations`} id="mutations">
          Mutations
        </Tab>
      </ReakitTabList>

      <TabPanel {...tab} className="focus:outline-none">
        <Nav items={queries} label="Queries" />
      </TabPanel>
      <TabPanel {...tab} className="focus:outline-none">
        <Nav items={mutations} label="Mutations" />
      </TabPanel>
    </div>
  );
}
